import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, Chip, CircularProgress, Tooltip,
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const COLUMNS = [
  { id: 'index',           label: 'Dataset',      align: 'left',   numeric: false },
  { id: 'doc_count',       label: 'Records',      align: 'right',  numeric: true  },
  { id: 'oldest',          label: 'Oldest',       align: 'left',   numeric: false, dateLike: true },
  { id: 'newest',          label: 'Newest',       align: 'left',   numeric: false, dateLike: true },
  { id: 'last_attempt_at', label: 'Last attempt', align: 'left',   numeric: false, dateLike: true },
  { id: 'last_attempt_outcome', label: 'Outcome', align: 'left',   numeric: false },
];

function compareRows(a, b, key, dir) {
  const av = a[key];
  const bv = b[key];
  // null/undefined sort to the bottom regardless of direction
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  let cmp;
  if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
  else cmp = String(av).localeCompare(String(bv));
  return dir === 'asc' ? cmp : -cmp;
}

function formatAttemptTime(iso) {
  if (!iso) return '—';
  // wrapper writes e.g. "2026-05-31T04:00:08+1000" — Date parses it fine.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Render in viewer-local time, short.
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function OutcomeChip({ outcome, slot }) {
  if (!outcome) return <Typography variant="body2" color="text.secondary">—</Typography>;
  const isOk = outcome === 'ok';
  const chip = (
    <Chip
      label={isOk ? 'OK' : outcome.charAt(0).toUpperCase() + outcome.slice(1)}
      size="small"
      color={isOk ? 'success' : 'error'}
      variant={isOk ? 'outlined' : 'filled'}
      sx={{ fontWeight: 600 }}
    />
  );
  return slot
    ? <Tooltip title={`scraper: ${slot}`}>{chip}</Tooltip>
    : chip;
}

export default function DatasetFreshnessTable() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [sortKey, setSortKey] = useState('index');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/data-sources/freshness`)
      .then(res => {
        if (cancelled) return;
        setRows(res.data.datasets || []);
        setGeneratedAt(res.data.generated_at || null);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message || 'failed to load');
      });
    return () => { cancelled = true; };
  }, []);

  const sortedRows = useMemo(() => {
    if (!rows) return [];
    return [...rows].sort((a, b) => compareRows(a, b, sortKey, sortDir));
  }, [rows, sortKey, sortDir]);

  const onSort = (key) => {
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'doc_count' || key === 'newest' || key === 'last_attempt_at' ? 'desc' : 'asc');
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Dataset freshness
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {generatedAt ? `Refreshed ${new Date(generatedAt).toLocaleString()}` : ''}
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Record-date range from Elasticsearch (the dataset's natural date field — sitting date for chamber records,
        <code> last_updated</code> for bills, <code>date_scraped</code> for reference data). “Last attempt” is when the
        scraper most recently ran for this dataset; click any column header to sort.
      </Typography>

      {error && <Typography color="error" variant="body2">Could not load: {error}</Typography>}
      {!rows && !error && <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>}

      {rows && (
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {COLUMNS.map(col => (
                  <TableCell key={col.id} align={col.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                    <TableSortLabel
                      active={sortKey === col.id}
                      direction={sortKey === col.id ? sortDir : 'asc'}
                      onClick={() => onSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map(r => (
                <TableRow key={r.index} hover>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.85rem' }}>
                    <Box>{r.index}</Box>
                    <Typography variant="caption" color="text.secondary">{r.label}</Typography>
                  </TableCell>
                  <TableCell align="right">{(r.doc_count ?? 0).toLocaleString()}</TableCell>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.85rem' }}>{r.oldest ?? '—'}</TableCell>
                  <TableCell sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.85rem' }}>{r.newest ?? '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.85rem' }}>{formatAttemptTime(r.last_attempt_at)}</TableCell>
                  <TableCell><OutcomeChip outcome={r.last_attempt_outcome} slot={r.last_attempt_slot} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
